import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import { Icone } from '../components/Icone';
import { BarreSites } from '../components/ui/BarreSites';
import { usePerimetre } from '../site/usePerimetre';
import { useSites } from '../lib/useSites';
import { useSansContrat } from '../lib/usePedagogique';
import { useAbsences } from '../lib/useAbsences';
import { useSyntheseEmargement } from '../lib/useEmargement';

const chiffre = (valeur: number | undefined): string => (valeur === undefined ? '—' : String(valeur));

export function PedHubPage(): JSX.Element {
  const { utilisateur, peutAcceder } = useAuth();
  const navigate = useNavigate();
  const { site } = usePerimetre();
  const { data: sansEntreprise } = useSansContrat(
    { site },
    { actif: peutAcceder('ped-sanscontrat') },
  );
  const { data: absences } = useAbsences(site, 'MOIS', peutAcceder('ped-absences'));
  // Ce qui demande un geste d'abord, puis les plus récentes.
  const lignesAbsences = useMemo(
    () =>
      [...(absences?.journees ?? [])]
        .sort(
          (a, b) =>
            Number(b.avertissement.palierDu !== null || b.information.etat === 'A_INFORMER') -
            Number(a.avertissement.palierDu !== null || a.information.etat === 'A_INFORMER'),
        )
        .slice(0, 3),
    [absences?.journees],
  );
  const { data: emargement } = useSyntheseEmargement(site, peutAcceder('emargement'));
  const signaturesDuJour = emargement?.presentiel;
  const partSignee =
    signaturesDuJour && signaturesDuJour.attendus > 0
      ? Math.round((signaturesDuJour.signes / signaturesDuJour.attendus) * 100)
      : 0;
  const { data: sitesData } = useSites();
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const afficherToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3000);
  };

  const isAssistant = utilisateur?.sousProfil === 'ASSISTANT';

  const sites = useMemo(() => sitesData?.sites ?? [], [sitesData]);
  const codesSites = useMemo(() => sites.map((s) => s.code), [sites]);

  // Statistiques dynamiques selon le périmètre d'antennes sélectionné
  const currentStats = useMemo(() => {
    if (site && site !== 'TOUS') {
      return { app: 160, integ: 2, abs: 2, seuils: 1, fiches: 1, attest: 1 };
    }
    const nbSites = Math.max(codesSites.length, 1);
    return {
      app: nbSites * 168,
      integ: nbSites * 2,
      abs: nbSites * 2,
      seuils: nbSites * 1,
      fiches: nbSites * 1,
      attest: nbSites * 1,
    };
  }, [site, codesSites]);

  const sections = [
    { ancre: 'integration', titre: "Intégration d'un nouvel apprenant", cible: 'ped-integration', resume: 'Intégration sous 8 jours' },
    { ancre: 'documents', titre: 'Documents à faire signer', cible: 'ped-documents', resume: 'documents signables' },
    { ancre: 'suivi-documentaire', titre: 'Statuts de suivi documentaire', cible: 'ped-documents', resume: 'triple statut documentaire' },
    { ancre: 'tests', titre: 'Tests de positionnement', cible: 'ped-tests', resume: 'tests corrigés par IA' },
    { ancre: 'absences', titre: 'Suivi des absences', cible: 'ped-absences', resume: 'absences et courriers' },
    { ancre: 'emargement', titre: 'Émargement présentiel & e-learning', cible: 'emargement', resume: 'émargement présentiel et e-learning' },
    { ancre: 'accompagnement', titre: 'Accompagnement personnalisé', cible: 'ped-accompagnement', resume: 'accompagnement' },
    { ancre: 'sans-entreprise', titre: 'Apprenants sans entreprise', cible: 'ped-sanscontrat', resume: 'apprenants sans entreprise' },
    { ancre: 'repertoire', titre: 'Répertoire entreprises & partenaires', cible: 'ped-repertoire', resume: 'répertoire par site' },
    { ancre: 'veille', titre: 'Veille réglementaire & numérique', cible: 'ped-veille', resume: 'veille réglementaire' },
    // Le sommaire ne nomme que les rubriques ouvertes au lecteur : le
    // responsable n'a plus le répertoire (TF-467), l'assistant(e) ni les
    // tests, ni la veille, ni « Sans entreprise » (TF-468).
  ].filter((section) => peutAcceder(section.cible));

  const inScope = (itemSite: string) => (!site || site === 'TOUS' ? true : itemSite === site);

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      {toastMessage && (
        <div className="fixed bottom-6 right-6 z-50 rounded-xl bg-codialis-navy px-5 py-3 text-sm font-medium text-white shadow-2xl transition-all">
          {toastMessage}
        </div>
      )}

      {/* En-tête */}
      <div className="flex flex-col items-start justify-between gap-4 lg:flex-row lg:items-center">
        <div>
          <h1 className="font-display text-2xl font-bold text-codialis-navy">
            {isAssistant ? 'Assistance pédagogique' : 'Pôle pédagogique'}
          </h1>
          <p className="mt-0.5 text-sm text-codialis-gray">
            Toutes vos rubriques de suivi réunies sur une seule page
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => navigate('/ped-integration')}
            className="rounded-xl bg-codialis-navy px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-codialis-deep"
          >
            Envoyer les convocations (3)
          </button>
        </div>
      </div>

      {/* Bannière Périmètre */}
      <div className="grid grid-cols-1 items-center gap-5 rounded-2xl bg-gradient-to-br from-codialis-navy to-codialis-blue p-4 sm:p-6 text-white lg:grid-cols-4">
        <div className="lg:col-span-3 min-w-0">
          <div className="mb-1 text-[11px] font-bold uppercase tracking-wider text-codialis-mint">
            Votre périmètre
          </div>
          <h2 className="font-display mb-1 text-xl font-bold">
            {sections.length} rubriques de suivi pédagogique
          </h2>
          <p className="text-sm text-white/70">
            {sections.map((section) => section.resume).join(', ')}.
          </p>
        </div>
        <div className="text-center">
          <div className="font-display text-5xl font-extrabold text-codialis-mint">
            {currentStats.app}
          </div>
          <div className="mt-1 text-xs text-white/60">
            {site && site !== 'TOUS' ? `apprenants à ${site}` : 'apprenants sur mon périmètre'}
          </div>
        </div>
      </div>

      <BarreSites />

      {/* 4 Compteurs KPI */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="stat-card rounded-2xl bg-white p-4 sm:p-5 shadow-card">
          <div className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
            Apprenants suivis
          </div>
          <div className="font-display mt-1 text-3xl font-extrabold text-codialis-navy">
            {currentStats.app}
          </div>
          <div className="mt-0.5 text-xs text-codialis-gray">
            {site && site !== 'TOUS'
              ? site
              : codesSites.length > 0
                ? codesSites.join(' · ')
                : 'Toutes les antennes'}
          </div>
        </div>

        <div className="stat-card rounded-2xl bg-white p-4 sm:p-5 shadow-card">
          <div className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
            Intégrations sous 8 j
          </div>
          <div className="font-display mt-1 text-3xl font-extrabold text-amber-600">
            {currentStats.integ}
          </div>
          <div className="mt-0.5 text-xs text-codialis-gray">
            convocations à envoyer
          </div>
        </div>

        <div className="stat-card rounded-2xl bg-white p-4 sm:p-5 shadow-card">
          <div className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
            Absences du jour
          </div>
          <div className="font-display mt-1 text-3xl font-extrabold text-red-600">
            {currentStats.abs}
          </div>
          <div className="mt-0.5 text-xs text-codialis-gray">
            {currentStats.seuils} seuil(s) dépassé(s)
          </div>
        </div>

        <div className="stat-card rounded-2xl bg-white p-4 sm:p-5 shadow-card">
          <div className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
            {isAssistant ? 'Attestations à délivrer' : 'Fiches séquence à valider'}
          </div>
          <div className={`font-display mt-1 text-3xl font-extrabold ${isAssistant ? 'text-codialis-teal' : 'text-codialis-sky'}`}>
            {isAssistant ? currentStats.attest : currentStats.fiches}
          </div>
          <div className="mt-0.5 text-xs text-codialis-gray">
            {isAssistant ? 'entrée en formation' : 'délai 8 jours'}
          </div>
        </div>
      </div>

      {/* Disposition avec Sommaire gauche et Blocs détaillés droite */}
      <div className="flex flex-col items-start gap-6 lg:flex-row">
        {/* Navigation sommaire */}
        <nav aria-label="Sommaire du pôle" className="w-full shrink-0 rounded-2xl bg-white p-3 shadow-card lg:sticky lg:top-20 lg:w-60">
          <div className="px-2 py-1.5 text-[10px] font-bold uppercase tracking-widest text-codialis-gray">
            Sommaire
          </div>
          <div className="relative flex gap-1 overflow-x-auto lg:flex-col">
            {sections.map((sec) => (
              <a
                key={sec.ancre}
                href={`#sec-${sec.ancre}`}
                className="flex shrink-0 items-center gap-2 rounded-xl px-2.5 py-2 transition hover:bg-gray-50 lg:shrink"
              >
                <span className="truncate text-xs font-medium text-codialis-navy">
                  {sec.titre}
                </span>
              </a>
            ))}
          </div>
        </nav>

        {/* Blocs détaillés */}
        <div className="w-full min-w-0 flex-1 space-y-6">
          {/* Section integration */}
          <section id="sec-integration" className="scroll-mt-24 overflow-hidden rounded-2xl bg-white shadow-card">
            <div className="flex flex-col justify-between gap-3 border-b border-gray-100 px-4 sm:px-6 py-4 sm:flex-row sm:items-center">
              <h3 className="font-display font-bold text-codialis-navy">
                Intégration d'un nouvel apprenant
              </h3>
              <Link to="/ped-integration" className="text-xs font-semibold text-codialis-sky hover:underline">
                Écran détaillé →
              </Link>
            </div>
            <div className="grid grid-cols-1 divide-y divide-gray-100 lg:grid-cols-3 lg:divide-x lg:divide-y-0">
              <div className="bg-gray-50/60 p-4 sm:p-5">
                <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-codialis-gray">
                  Ce que couvre cette rubrique
                </div>
                <div className="space-y-1.5 text-xs text-codialis-gray">
                  <div className="flex items-start gap-2">
                    <Icone nom="medaille" className="mt-0.5 h-3.5 w-3.5 text-codialis-teal" />
                    <span>Alerte dès qu'un nouveau contrat est enregistré</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Icone nom="medaille" className="mt-0.5 h-3.5 w-3.5 text-codialis-teal" />
                    <span>Alerte spécifique si l'entrée a lieu sous 8 jours</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Icone nom="medaille" className="mt-0.5 h-3.5 w-3.5 text-codialis-teal" />
                    <span>Convocation pré-remplie depuis les données du dossier</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Icone nom="medaille" className="mt-0.5 h-3.5 w-3.5 text-codialis-teal" />
                    <span>Notification automatique du formateur référent</span>
                  </div>
                </div>
              </div>
              <div className="divide-y divide-gray-100 lg:col-span-2 min-w-0">
                {[
                  { nom: 'Yohan Bègue', form: 'Conseiller de vente (CDV)', entry: 'entrée 08/09', st: 'J-6 · convocation à envoyer', lvl: 'badge-danger', notif: 'T. Léger notifié' },
                  { nom: 'Kevin Ramassamy', form: 'Employé commercial (EC)', entry: 'entrée 05/09', st: 'J-9 · dossier incomplet', lvl: 'badge-warning', notif: 'M. Boyer notifié' },
                  { nom: 'Anaïs Grondin', form: "Assistant d'unité marchande (AMUM)", entry: 'entrée 15/09', st: 'J-13', lvl: 'badge-warning', notif: 'M. Boyer notifié' },
                  { nom: 'Léa Fontaine', form: "Manager(se) d'unité marchande (MUM)", entry: 'entrée 01/09', st: 'Convocation envoyée', lvl: 'badge-success', notif: 'T. Léger notifié' },
                ]
                  .map((r, i) => {
                    const rSite = codesSites.length > 0 ? codesSites[i % codesSites.length]! : 'SITE';
                    return {
                      ...r,
                      site: rSite,
                      formComplet: `${r.form} · ${rSite} · ${r.entry}`,
                    };
                  })
                  .filter((r) => inScope(r.site))
                  .map((r, i) => (
                    <div key={i} className="flex items-center gap-3 px-5 py-3 transition hover:bg-gray-50">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-codialis-sky to-codialis-blue text-[10px] font-bold text-white">
                        {r.nom.split(' ').map((x) => x[0]).join('')}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-codialis-navy">{r.nom}</div>
                        <div className="truncate text-[11px] text-codialis-gray">
                          {r.formComplet} · {r.notif}
                        </div>
                      </div>
                      <span className={`${r.lvl} rounded px-2 py-0.5 text-[10px] font-bold`}>
                        {r.st}
                      </span>
                      <button
                        type="button"
                        onClick={() => afficherToast(`Convocation pré-remplie ouverte pour ${r.nom}`)}
                        className="shrink-0 text-xs font-semibold text-codialis-sky hover:underline"
                      >
                        Convocation
                      </button>
                    </div>
                  ))}
                <div className="bg-codialis-mint/10 px-5 py-3 text-xs text-codialis-navy">
                  Tous les champs de la convocation (formation, site, salle, date, formateur référent, pièces à apporter)
                  proviennent du dossier, sans aucune saisie manuelle.
                </div>
              </div>
            </div>
          </section>

          {/* Section documents */}
          <section id="sec-documents" className="scroll-mt-24 overflow-hidden rounded-2xl bg-white shadow-card">
            <div className="flex flex-col justify-between gap-3 border-b border-gray-100 px-4 sm:px-6 py-4 sm:flex-row sm:items-center">
              <h3 className="font-display font-bold text-codialis-navy">
                Documents pédagogiques à faire signer
              </h3>
              <Link to="/ped-documents" className="text-xs font-semibold text-codialis-sky hover:underline">
                Documents pédagogiques →
              </Link>
            </div>
            <div className="grid grid-cols-1 divide-y divide-gray-100 lg:grid-cols-3 lg:divide-x lg:divide-y-0">
              <div className="bg-gray-50/60 p-4 sm:p-5">
                <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-codialis-gray">
                  Documents obligatoires
                </div>
                <div className="space-y-1 text-xs text-codialis-gray">
                  <div>• Règlement intérieur</div>
                  <div>• Livret d'accueil</div>
                  <div>• Charte d'engagement</div>
                  <div>• Autorisation droit à l'image</div>
                  <div>• Fiche synthétisant le REAC</div>
                  <div>• Signature sur tablette</div>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-2 p-4 sm:p-5 sm:grid-cols-2 lg:col-span-2 min-w-0">
                {[
                  { n: 'Règlement intérieur', st: 'Signé le 23/07', lvl: 'badge-success', tag: 'OK' },
                  { n: "Livret d'accueil", st: 'Signé le 23/07', lvl: 'badge-success', tag: 'OK' },
                  { n: "Charte d'engagement", st: 'À signer', lvl: 'badge-warning', tag: 'Signer' },
                  { n: "Autorisation droit à l'image", st: 'À signer', lvl: 'badge-warning', tag: 'Signer' },
                  { n: 'Fiche REAC · RNCP 37 098', st: 'Remise le 23/07', lvl: 'badge-success', tag: 'OK' },
                  { n: "Attestation d'entrée en formation", st: 'Délivrée', lvl: 'badge-mint', tag: 'OK' },
                ].map((doc, idx) => (
                  <div key={idx} className="flex items-center gap-3 rounded-xl bg-gray-50 px-3 py-2.5">
                    <Icone nom="document" className="h-4 w-4 shrink-0 text-codialis-navy" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-codialis-navy">{doc.n}</div>
                      <div className="text-[11px] text-codialis-gray">{doc.st}</div>
                    </div>
                    <span className={`${doc.lvl} rounded px-2 py-0.5 text-[10px] font-bold`}>
                      {doc.tag}
                    </span>
                  </div>
                ))}
                <div className="mt-2 flex gap-2 sm:col-span-2 min-w-0">
                  <button
                    type="button"
                    onClick={() => navigate('/ped-documents')}
                    className="rounded-lg bg-codialis-navy px-4 py-2 text-xs font-semibold text-white transition hover:bg-codialis-deep"
                  >
                    Ouvrir les documents à signer
                  </button>
                  <button
                    type="button"
                    onClick={() => afficherToast('Documents envoyés par e-mail')}
                    className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-xs font-semibold text-codialis-navy transition hover:bg-gray-50"
                  >
                    Envoyer par e-mail
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* Section absences */}
          <section id="sec-absences" className="scroll-mt-24 overflow-hidden rounded-2xl bg-white shadow-card">
            <div className="flex flex-col justify-between gap-3 border-b border-gray-100 px-4 sm:px-6 py-4 sm:flex-row sm:items-center">
              <h3 className="font-display font-bold text-codialis-navy">
                Suivi des absences & avertissements
              </h3>
              <Link to="/ped-absences" className="text-xs font-semibold text-codialis-sky hover:underline">
                Écran dédié absences →
              </Link>
            </div>
            {/* Les chiffres et les lignes de l'écran « Absences & avertissements »,
                sur la même antenne : le hub n'en tient aucun à lui. */}
            <div className="grid grid-cols-1 divide-y divide-gray-100 lg:grid-cols-3 lg:divide-x lg:divide-y-0">
              <div className="bg-gray-50/60 p-4 sm:p-5">
                <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-codialis-gray">
                  Règles & alertes
                </div>
                <p className="text-xs text-codialis-gray">
                  À la clôture de chaque demi-journée, l'entreprise et le maître d'apprentissage sont
                  informés par mail, matin, après-midi ou journée complète. Au seuil de la formation,
                  puis à chaque multiple du seuil, le courrier d'avertissement part aux parties.
                </p>
                <div className="mt-3 grid grid-cols-2 gap-2 text-center">
                  {[
                    ['À informer', absences?.compteurs.aInformer],
                    ['Courriers dus', absences?.compteurs.courriersDus],
                    ['Seuil atteint', absences?.compteurs.seuilAtteint],
                    ['Justificatifs', absences?.compteurs.justificatifsAExaminer],
                  ].map(([libelle, valeur]) => (
                    <div key={String(libelle)} className="rounded-lg border border-gray-100 bg-white p-2">
                      <div className="font-display text-lg font-bold text-codialis-navy">
                        {chiffre(valeur as number | undefined)}
                      </div>
                      <div className="text-[10px] text-codialis-gray">{libelle}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="min-w-0 divide-y divide-gray-100 lg:col-span-2">
                {lignesAbsences.length === 0 && (
                  <p className="px-5 py-6 text-sm text-codialis-gray">
                    {absences ? 'Aucune absence sur les 30 derniers jours.' : 'Chargement des absences…'}
                  </p>
                )}
                {lignesAbsences.map((ligne) => (
                  <div key={ligne.cle} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 transition hover:bg-gray-50">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-codialis-navy">{ligne.apprenant}</div>
                      <div className="text-xs text-codialis-gray">
                        {ligne.jourLisible} · {ligne.typeLibelle} · {ligne.entreprise?.denomination ?? 'sans entreprise'}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`badge-${ligne.avertissement.palierDu !== null ? 'danger' : ligne.information.ton} rounded px-2 py-0.5 text-[10px] font-bold`}>
                        {ligne.avertissement.palierDu !== null ? 'Courrier dû' : ligne.information.libelle}
                      </span>
                      <Link to="/ped-absences" className="text-xs font-semibold text-codialis-sky hover:underline">
                        Consulter
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Section emargement */}
          {peutAcceder('emargement') && (
            <section id="sec-emargement" className="scroll-mt-24 overflow-hidden rounded-2xl bg-white shadow-card">
              <div className="flex flex-col justify-between gap-3 border-b border-gray-100 px-4 sm:px-6 py-4 sm:flex-row sm:items-center">
                <h3 className="font-display font-bold text-codialis-navy">
                  Émargement présentiel & e-learning
                </h3>
                <Link
                  to="/emargement"
                  className="inline-flex items-center text-xs font-semibold text-codialis-sky hover:underline [@media(pointer:coarse)]:min-h-[40px]"
                >
                  Écran d'émargement →
                </Link>
              </div>
              {/* Les feuilles du jour telles que l'émargement les tient : le hub
                  n'en compte aucune à lui. */}
              <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 sm:p-5">
                <div className="min-w-0 rounded-xl bg-gray-50 p-4">
                  <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-codialis-gray">
                    Présentiel
                  </div>
                  <div className="text-sm font-semibold text-codialis-navy">Feuilles d'émargement du jour</div>
                  <p className="mt-1 text-xs text-codialis-gray">
                    Signature apprenant et formateur par demi-journée, sur tablette. PV archivé à la
                    clôture, feuille d'audit par semaine.
                  </p>
                  {signaturesDuJour && signaturesDuJour.feuilles > 0 ? (
                    <div className="mt-3 flex items-center gap-2">
                      <div
                        className="progress-track h-1.5 min-w-0 flex-1 overflow-hidden rounded-full"
                        role="progressbar"
                        aria-valuenow={partSignee}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-label="Signatures recueillies"
                      >
                        <div className="progress-bar h-full" style={{ width: `${partSignee}%` }} />
                      </div>
                      <span className="shrink-0 text-xs font-bold text-codialis-navy">
                        {signaturesDuJour.signes}/{signaturesDuJour.attendus}
                      </span>
                    </div>
                  ) : (
                    <p className="mt-3 text-xs text-codialis-gray">
                      {emargement ? 'Aucune feuille ouverte aujourd’hui.' : 'Chargement…'}
                    </p>
                  )}
                  {signaturesDuJour && (signaturesDuJour.aOuvrir > 0 || signaturesDuJour.aCloturer > 0) && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {signaturesDuJour.aOuvrir > 0 && (
                        <span className="badge-warning rounded px-2 py-0.5 text-[10px] font-bold">
                          {signaturesDuJour.aOuvrir} à ouvrir
                        </span>
                      )}
                      {signaturesDuJour.aCloturer > 0 && (
                        <span className="badge-neutral rounded px-2 py-0.5 text-[10px] font-bold">
                          {signaturesDuJour.aCloturer} à clôturer
                        </span>
                      )}
                    </div>
                  )}
                  <Link
                    to="/emargement"
                    className="mt-3 inline-flex min-h-[32px] items-center text-xs font-bold text-codialis-sky hover:underline [@media(pointer:coarse)]:min-h-[40px]"
                  >
                    Ouvrir la feuille →
                  </Link>
                </div>
                <div className="min-w-0 rounded-xl bg-gray-50 p-4">
                  <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-codialis-gray">
                    E-learning
                  </div>
                  <div className="text-sm font-semibold text-codialis-navy">
                    Émargement numérique · caméra obligatoire
                  </div>
                  <p className="mt-1 text-xs text-codialis-gray">
                    L'apprenant émarge dans la salle, caméra allumée ; une coupure s'affiche au
                    formateur.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {emargement && emargement.distance.classes === 0 && (
                      <span className="text-xs text-codialis-gray">Aucune classe à distance aujourd’hui.</span>
                    )}
                    {emargement && emargement.distance.classes > 0 && (
                      <span className="badge-neutral rounded px-2 py-0.5 text-[10px] font-bold">
                        {emargement.distance.classes} classe{emargement.distance.classes > 1 ? 's' : ''} ·{' '}
                        {emargement.distance.enDirect} en direct
                      </span>
                    )}
                    {emargement && emargement.distance.enDirect > 0 && (
                      <>
                        <span className="badge-success rounded px-2 py-0.5 text-[10px] font-bold">
                          {emargement.distance.camerasAllumees} caméra{emargement.distance.camerasAllumees > 1 ? 's' : ''} active{emargement.distance.camerasAllumees > 1 ? 's' : ''}
                        </span>
                        {emargement.distance.camerasCoupees > 0 && (
                          <span className="badge-danger rounded px-2 py-0.5 text-[10px] font-bold">
                            {emargement.distance.camerasCoupees} coupée{emargement.distance.camerasCoupees > 1 ? 's' : ''}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                  {peutAcceder('form-elearning') && (
                    <Link
                      to="/form-elearning"
                      className="mt-3 inline-flex min-h-[32px] items-center text-xs font-bold text-codialis-sky hover:underline [@media(pointer:coarse)]:min-h-[40px]"
                    >
                      Voir la salle virtuelle →
                    </Link>
                  )}
                </div>
              </div>
            </section>
          )}

          {/* Section sans-entreprise */}
          {peutAcceder('ped-sanscontrat') && (
            <section id="sec-sans-entreprise" className="scroll-mt-24 overflow-hidden rounded-2xl bg-white shadow-card">
              <div className="flex flex-col justify-between gap-3 border-b border-gray-100 px-4 sm:px-6 py-4 sm:flex-row sm:items-center">
                <h3 className="font-display font-bold text-codialis-navy">
                  Apprenants sans entreprise
                </h3>
                <Link to="/ped-sanscontrat" className="text-xs font-semibold text-codialis-sky hover:underline">
                  Voir tous les sans-contrat →
                </Link>
              </div>
              <div className="p-4 sm:p-5">
                {/* Les chiffres de l'écran « Sans entreprise », sur la même antenne :
                    le hub n'en tient aucun à lui. */}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div className="rounded-xl border border-gray-100 bg-gray-50 p-3 text-center">
                    <div className="font-display text-2xl font-bold text-codialis-navy">
                      {chiffre(sansEntreprise?.compteurs.sansEntreprise)}
                    </div>
                    <div className="text-xs text-codialis-gray">Inscrits sans entreprise</div>
                  </div>
                  <div className="rounded-xl border border-gray-100 bg-gray-50 p-3 text-center">
                    <div className="font-display text-2xl font-bold text-amber-600">
                      {chiffre(sansEntreprise?.compteurs.immersions)}
                    </div>
                    <div className="text-xs text-codialis-gray">Immersions planifiées</div>
                  </div>
                  <div className="rounded-xl border border-gray-100 bg-gray-50 p-3 text-center">
                    <div className="font-display text-2xl font-bold text-red-600">
                      {chiffre(sansEntreprise?.compteurs.rechercheLongue)}
                    </div>
                    <div className="text-xs text-codialis-gray">&gt; 3 mois de recherche</div>
                  </div>
                </div>
                {peutAcceder('com-matching') && (
                  <div className="mt-4 flex gap-2">
                    <button
                      type="button"
                      onClick={() => navigate('/com-matching')}
                      className="rounded-lg bg-codialis-navy px-4 py-2 text-xs font-semibold text-white transition hover:bg-codialis-deep"
                    >
                      Lancer le matching entreprise
                    </button>
                  </div>
                )}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
